import { useEffect, useMemo, useRef } from 'react';
import { BufferGeometry, Float32BufferAttribute } from 'three';
import type { Material } from 'three';
import type { DrivingCourseOption, CourseCheckpoint } from '../data/courseOptions';
import type { CourseObstacle, CoursePoint, CourseRoute } from '../model/drivingCourse';
import { sampleDrivingTerrain } from '../model/drivingTerrain';
import { DRIVING_FLOOR_RADIUS } from '../model/stageGround';
import type { RuntimeWeather } from '../model/weatherTransition';
import type { AsphaltTextures } from './materials/asphaltSurface';
import { useWeatherRoadMaterials } from './WeatherAppliers';

const ROAD_Y = 0.001;
const PAINT_Y = 0.004;
const EDGE_WIDTH = 0.42;

function tangentAt(points: readonly CoursePoint[], index: number, closed: boolean): CoursePoint {
  const previous = index === 0 ? (closed ? points[points.length - 1] : points[0]) : points[index - 1];
  const next = index === points.length - 1 ? (closed ? points[0] : points[index]) : points[index + 1];
  const dx = next.x - previous.x;
  const dz = next.z - previous.z;
  const length = Math.max(1e-6, Math.hypot(dx, dz));
  return { x: dx / length, z: dz / length };
}

/** 중심선에서 offset만큼 옮긴 width 폭의 리본을 만듭니다. */
function createStripGeometry(
  route: CourseRoute,
  centerOffset: number,
  width: number,
  yOffset: number
): BufferGeometry {
  const positions: number[] = [];
  const uvs: number[] = [];
  const indices: number[] = [];
  const halfWidth = width / 2;
  const points = route.points;

  points.forEach((point, index) => {
    const tangent = tangentAt(points, index, route.closed);
    const rightX = -tangent.z;
    const rightZ = tangent.x;
    for (const side of [-1, 1]) {
      const lateral = centerOffset + side * halfWidth;
      const x = point.x + rightX * lateral;
      const z = point.z + rightZ * lateral;
      const y = sampleDrivingTerrain(x, z).height + yOffset;
      positions.push(x, y, z);
      // DrivingGround가 world-size repeat을 주므로 월드 좌표를 0..1 floor UV로 바꿉니다.
      uvs.push(
        (x + DRIVING_FLOOR_RADIUS) / (DRIVING_FLOOR_RADIUS * 2),
        (-z + DRIVING_FLOOR_RADIUS) / (DRIVING_FLOOR_RADIUS * 2)
      );
    }
  });

  const segmentCount = route.closed ? points.length : points.length - 1;
  for (let index = 0; index < segmentCount; index += 1) {
    const next = (index + 1) % points.length;
    const a = index * 2;
    const b = a + 1;
    const c = next * 2;
    const d = c + 1;
    // 위(+Y)를 향하는 winding. 반대로 쓰면 기본 FrontSide 재질이 주행 카메라에서 컬링됩니다.
    indices.push(a, b, c, b, d, c);
  }

  const geometry = new BufferGeometry();
  geometry.setAttribute('position', new Float32BufferAttribute(positions, 3));
  geometry.setAttribute('uv', new Float32BufferAttribute(uvs, 2));
  geometry.setIndex(indices);
  geometry.computeVertexNormals();
  geometry.computeBoundingSphere();
  return geometry;
}

function nearestTangent(route: CourseRoute, point: CoursePoint): CoursePoint {
  let nearestIndex = 0;
  let minimum = Number.POSITIVE_INFINITY;
  route.points.forEach((candidate, index) => {
    const distance = (candidate.x - point.x) ** 2 + (candidate.z - point.z) ** 2;
    if (distance < minimum) {
      minimum = distance;
      nearestIndex = index;
    }
  });
  return tangentAt(route.points, nearestIndex, route.closed);
}

function createGateGeometry(route: CourseRoute, checkpoint: CourseCheckpoint): BufferGeometry {
  const tangent = checkpoint.routeIndex === undefined
    ? nearestTangent(route, checkpoint)
    : tangentAt(
        route.points,
        Math.min(route.points.length - 1, Math.max(0, checkpoint.routeIndex)),
        route.closed
      );
  const right = { x: -tangent.z, z: tangent.x };
  const halfDepth = checkpoint.id === 'finish' ? 0.65 : 0.25;
  const halfWidth = route.roadHalfWidth;
  const corners = [
    { x: checkpoint.x - right.x * halfWidth - tangent.x * halfDepth, z: checkpoint.z - right.z * halfWidth - tangent.z * halfDepth },
    { x: checkpoint.x + right.x * halfWidth - tangent.x * halfDepth, z: checkpoint.z + right.z * halfWidth - tangent.z * halfDepth },
    { x: checkpoint.x - right.x * halfWidth + tangent.x * halfDepth, z: checkpoint.z - right.z * halfWidth + tangent.z * halfDepth },
    { x: checkpoint.x + right.x * halfWidth + tangent.x * halfDepth, z: checkpoint.z + right.z * halfWidth + tangent.z * halfDepth },
  ];
  const positions = corners.flatMap((corner) => [
    corner.x,
    sampleDrivingTerrain(corner.x, corner.z).height + PAINT_Y,
    corner.z,
  ]);
  const geometry = new BufferGeometry();
  geometry.setAttribute('position', new Float32BufferAttribute(positions, 3));
  geometry.setIndex([0, 1, 2, 1, 3, 2]);
  geometry.computeVertexNormals();
  return geometry;
}

function CourseCone({ obstacle }: { obstacle: CourseObstacle }) {
  const y = sampleDrivingTerrain(obstacle.x, obstacle.z).height;
  const radius = obstacle.collider.radius;
  const bodyRadius = radius * 0.73;
  return (
    <group position={[obstacle.x, y, obstacle.z]}>
      <mesh position={[0, obstacle.height / 2, 0]} castShadow={false} receiveShadow={false}>
        <coneGeometry args={[bodyRadius, obstacle.height, 12]} />
        <meshStandardMaterial color="#ef682f" roughness={0.72} />
      </mesh>
      <mesh position={[0, obstacle.height * 0.54, 0]} castShadow={false} receiveShadow={false}>
        <cylinderGeometry args={[radius * 0.5, radius * 0.57, obstacle.height * 0.155, 12]} />
        <meshStandardMaterial color="#f2f0e9" roughness={0.68} />
      </mesh>
      <mesh position={[0, 0.025, 0]} castShadow={false} receiveShadow={false}>
        <cylinderGeometry args={[radius, radius, 0.05, 12]} />
        <meshStandardMaterial color="#25282c" roughness={0.82} />
      </mesh>
    </group>
  );
}

export interface CourseRouteSurfaceProps {
  course: DrivingCourseOption;
  asphalt: AsphaltTextures | null;
  weather: RuntimeWeather;
}

/** 신규 중심선 코스의 도로·경계선·게이트·콘을 한 데이터에서 렌더합니다. */
export function CourseRouteSurface({ course, asphalt, weather }: CourseRouteSurfaceProps) {
  const route = course.route;
  const road = useMemo(
    () => createStripGeometry(route, 0, route.roadHalfWidth * 2, ROAD_Y),
    [route]
  );
  const leftEdge = useMemo(
    () => createStripGeometry(route, -route.roadHalfWidth + EDGE_WIDTH / 2, EDGE_WIDTH, PAINT_Y),
    [route]
  );
  const rightEdge = useMemo(
    () => createStripGeometry(route, route.roadHalfWidth - EDGE_WIDTH / 2, EDGE_WIDTH, PAINT_Y),
    [route]
  );
  const gates = useMemo(
    () => course.checkpoints.map((checkpoint) => createGateGeometry(route, checkpoint)),
    [course.checkpoints, route]
  );

  useEffect(
    () => () => {
      road.dispose();
      leftEdge.dispose();
      rightEdge.dispose();
      gates.forEach((geometry) => geometry.dispose());
    },
    [road, leftEdge, rightEdge, gates]
  );

  const roadMaterialRef = useRef<Material | Material[] | null>(null);
  useWeatherRoadMaterials(weather, { asphalt: roadMaterialRef });

  return (
    <>
      <mesh geometry={road} receiveShadow={false}>
        {asphalt === null ? (
          <meshStandardMaterial ref={roadMaterialRef} color="#3a3d42" roughness={0.72} metalness={0.04} />
        ) : (
          <meshStandardMaterial
            ref={roadMaterialRef}
            map={asphalt.color}
            normalMap={asphalt.normal}
            roughnessMap={asphalt.roughness}
            roughness={0.72}
            metalness={0.04}
            envMapIntensity={0.35}
            polygonOffset
            polygonOffsetFactor={-1}
            polygonOffsetUnits={-1}
          />
        )}
      </mesh>
      {[leftEdge, rightEdge].map((geometry, index) => (
        <mesh key={index === 0 ? 'left' : 'right'} geometry={geometry}>
          <meshStandardMaterial
            color="#ececee"
            roughness={0.65}
            polygonOffset
            polygonOffsetFactor={-2}
            polygonOffsetUnits={-2}
          />
        </mesh>
      ))}
      {gates.map((geometry, index) => (
        <mesh key={course.checkpoints[index].id} geometry={geometry}>
          <meshStandardMaterial
            color={course.checkpoints[index].id === 'finish' ? '#f4f4f4' : '#e5b84d'}
            roughness={0.68}
            transparent
            opacity={course.checkpoints[index].id === 'finish' ? 0.95 : 0.5}
            depthWrite={false}
          />
        </mesh>
      ))}
      {route.obstacles?.map((obstacle) => (
        <CourseCone key={obstacle.id} obstacle={obstacle} />
      ))}
    </>
  );
}
